import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  extractIndividualFromConversation,
  getOnboardingChatResponse,
} from "../services/aiService";
import { createIndividual } from "../services/individualService";
import {
  ArrowLeft, Send, Mic, MicOff, Check, X, Edit3,
  User, AlertTriangle, Loader, ChevronDown,
} from "lucide-react";

export default function OnboardingChat() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  
  // Mode: "new" for new individual, "update" for editing existing
  const mode = location.state?.mode || "new";
  const existingRecord = location.state?.individual || null;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(existingRecord || {});
  const [missingFields, setMissingFields] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [editField, setEditField] = useState(null);
  const [editValue, setEditValue] = useState("");

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const conversationHistoryRef = useRef("");

  // Auto-scroll to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send initial greeting
  useEffect(() => {
    const greeting = mode === "update"
      ? `I have ${existingRecord?.firstName} ${existingRecord?.lastName}'s record pulled up. What needs to be updated?`
      : "Tell me about the new individual you're adding to your caseload. You can say everything at once, or I'll walk you through it.";
    
    setMessages([{ role: "assistant", content: greeting }]);
  }, []);

  // Speech recognition setup
  const startListening = useCallback(() => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    let finalTranscript = "";

    recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + " ";
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setInput(finalTranscript + interim);
    };

    recognition.onerror = (event) => {
      console.error("Speech error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      if (finalTranscript.trim()) {
        setInput(finalTranscript.trim());
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, []);

  // Process user message
  const handleSend = async () => {
    const text = input.trim();
    if (!text || isProcessing) return;

    // Add user message
    const newMessages = [...messages, { role: "user", content: text }];
    setMessages(newMessages);
    setInput("");
    setIsProcessing(true);

    // Build full conversation for extraction
    conversationHistoryRef.current += `\nAgent: ${text}`;

    try {
      // Extract structured data from the full conversation
      const extraction = await extractIndividualFromConversation(
        conversationHistoryRef.current,
        extractedData
      );

      if (extraction.success) {
        setExtractedData(extraction.data);
        setMissingFields(extraction.missingRequired || []);

        // Check if we have all required fields
        const allRequired = !extraction.missingRequired || extraction.missingRequired.length === 0;
        
        if (allRequired && text.toLowerCase().match(/save|looks good|that's it|that's all|confirm|yes|submit|done|correct|perfect/)) {
          setShowConfirmation(true);
          setMessages(prev => [...prev, {
            role: "assistant",
            content: `Here's what I've captured for ${extraction.data.firstName} ${extraction.data.lastName}. Review the details below and tap Save when ready.`,
          }]);
        } else {
          // Get conversational AI response
          const chatMessages = newMessages.map(m => ({
            role: m.role === "assistant" ? "assistant" : "user",
            content: m.content,
          }));

          const chatResp = await getOnboardingChatResponse(chatMessages, extraction);
          
          const assistantMsg = chatResp.success ? chatResp.message : 
            (allRequired ? `Got it. I have everything I need for ${extraction.data.firstName} ${extraction.data.lastName}. Say "save" or "looks good" when you're ready, or keep adding details.` :
            extraction.nextQuestion || "Could you tell me more?");

          conversationHistoryRef.current += `\nAssistant: ${assistantMsg}`;
          setMessages(prev => [...prev, { role: "assistant", content: assistantMsg }]);
        }
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "I had trouble processing that. Could you try again?",
        }]);
      }
    } catch (e) {
      console.error("Processing error:", e);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Something went wrong. Let me try again — what were you saying?",
      }]);
    }

    setIsProcessing(false);
  };

  // Save individual
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await createIndividual(extractedData);
      if (result.success) {
        navigate(`/individual/${result.id}`, {
          state: { justCreated: true },
          replace: true,
        });
      } else {
        alert("Failed to save: " + (result.error || "Unknown error"));
      }
    } catch (e) {
      alert("Error saving: " + e.message);
    }
    setIsSaving(false);
  };

  // Edit a field from confirmation card
  const handleEditField = (field, currentValue) => {
    setEditField(field);
    setEditValue(Array.isArray(currentValue) ? currentValue.join(", ") : currentValue || "");
  };

  const handleSaveEdit = () => {
    if (editField) {
      const value = editField === "diagnoses" || editField === "medications" || editField === "allergies"
        ? editValue.split(",").map(s => s.trim()).filter(Boolean)
        : editValue;
      setExtractedData(prev => ({ ...prev, [editField]: value }));
      setEditField(null);
      setEditValue("");
    }
  };

  const fieldLabel = (key) => {
    const labels = {
      firstName: "First Name", lastName: "Last Name", dob: "Date of Birth",
      gender: "Gender", phone: "Phone", email: "Email", address: "Address",
      county: "County", emergencyContact: "Emergency Contact", emergencyPhone: "Emergency Phone",
      emergencyRelation: "Relationship", diagnoses: "Diagnoses", medications: "Medications",
      riskLevel: "Risk Level", allergies: "Allergies", medicaidId: "Medicaid ID",
      insuranceProvider: "Insurance", program: "Program", contactFrequency: "Contact Frequency",
      assignToAgent: "Assigned Agent",
      onboardingNotes: "Notes", specialConsiderations: "Special Considerations",
    };
    return labels[key] || key;
  };

  const displayFields = [
    "firstName", "lastName", "dob", "gender", "phone", "address", "county",
    "program", "riskLevel", "contactFrequency", "assignToAgent", "diagnoses", "medications",
    "allergies", "medicaidId", "insuranceProvider", "emergencyContact", "emergencyPhone", "emergencyRelation",
    "onboardingNotes", "specialConsiderations",
  ];

  return (
    <div style={{ minHeight: "100vh", background: "#0a0f1a", color: "#e2e8f0", fontFamily: "'DM Sans', -apple-system, sans-serif", display: "flex", flexDirection: "column" }}>

      {/* Header */}
      <header style={{
        padding: "16px 20px", display: "flex", alignItems: "center", gap: 12,
        background: "linear-gradient(135deg, #0f1729, #162033)",
        borderBottom: "1px solid rgba(99,179,237,0.1)",
        position: "sticky", top: 0, zIndex: 50,
      }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: "#a0aec0" }}>
          <ArrowLeft size={20} />
        </button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800 }}>
            {mode === "update" ? "Update Individual" : "New Individual"}
          </div>
          <div style={{ fontSize: 10, color: "#718096", fontWeight: 600 }}>
            {mode === "update" ? "Tell me what changed" : "Just tell me about them"}
          </div>
        </div>
      </header>

      {/* Chat Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: "flex",
            justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
            marginBottom: 12,
          }}>
            <div style={{
              maxWidth: "85%",
              padding: "10px 14px",
              borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              background: msg.role === "user"
                ? "linear-gradient(135deg, #3182ce, #2b6cb0)"
                : "rgba(15,23,42,0.7)",
              border: msg.role === "user" ? "none" : "1px solid rgba(99,179,237,0.08)",
              fontSize: 13,
              lineHeight: 1.6,
              color: msg.role === "user" ? "white" : "#e2e8f0",
            }}>
              {msg.content}
            </div>
          </div>
        ))}

        {isProcessing && (
          <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 12 }}>
            <div style={{
              padding: "10px 14px", borderRadius: "16px 16px 16px 4px",
              background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
            }}>
              <Loader size={16} style={{ color: "#63b3ed", animation: "spin 1s linear infinite" }} />
            </div>
          </div>
        )}

        {/* Confirmation Card */}
        {showConfirmation && (
          <div style={{
            background: "rgba(15,23,42,0.7)",
            border: "1px solid rgba(79,209,197,0.15)",
            borderRadius: 16, padding: 20, marginTop: 8,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <User size={16} style={{ color: "#4fd1c5" }} />
              <span style={{ fontSize: 14, fontWeight: 800 }}>Confirm Individual Record</span>
            </div>

            {displayFields.map(field => {
              const value = extractedData[field];
              if (!value || (Array.isArray(value) && value.length === 0)) return null;

              const displayValue = Array.isArray(value) ? value.join(", ") : value;

              return (
                <div key={field} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid rgba(99,179,237,0.04)",
                }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#4a5568", textTransform: "uppercase", letterSpacing: 1 }}>
                      {fieldLabel(field)}
                    </div>
                    {editField === field ? (
                      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                        <input
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          style={{
                            flex: 1, padding: "4px 8px", borderRadius: 6, fontSize: 13,
                            background: "rgba(15,23,42,0.5)", border: "1px solid rgba(99,179,237,0.15)",
                            color: "#e2e8f0", outline: "none",
                          }}
                          autoFocus
                          onKeyDown={e => e.key === "Enter" && handleSaveEdit()}
                        />
                        <button onClick={handleSaveEdit} style={{ background: "none", border: "none", cursor: "pointer" }}>
                          <Check size={14} style={{ color: "#68d391" }} />
                        </button>
                        <button onClick={() => setEditField(null)} style={{ background: "none", border: "none", cursor: "pointer" }}>
                          <X size={14} style={{ color: "#fc8181" }} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 13, color: "#e2e8f0", marginTop: 2 }}>
                        {field === "riskLevel" ? (
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                            background: value === "high" ? "rgba(252,129,129,0.15)" : value === "moderate" ? "rgba(236,201,75,0.1)" : "rgba(104,211,145,0.1)",
                            color: value === "high" ? "#fc8181" : value === "moderate" ? "#ecc94b" : "#68d391",
                          }}>{value}</span>
                        ) : displayValue}
                      </div>
                    )}
                  </div>
                  {editField !== field && (
                    <button onClick={() => handleEditField(field, value)} style={{
                      background: "none", border: "none", cursor: "pointer", padding: 4,
                    }}>
                      <Edit3 size={12} style={{ color: "#4a5568" }} />
                    </button>
                  )}
                </div>
              );
            })}

            {/* Missing fields warning */}
            {missingFields.length > 0 && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 8,
                background: "rgba(236,201,75,0.05)", border: "1px solid rgba(236,201,75,0.1)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#ecc94b", fontWeight: 700 }}>
                  <AlertTriangle size={12} /> Missing: {missingFields.join(", ")}
                </div>
                <div style={{ fontSize: 10, color: "#718096", marginTop: 4 }}>
                  You can still save — add these details later.
                </div>
              </div>
            )}

            {/* Save / Cancel */}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button onClick={handleSave} disabled={isSaving} style={{
                flex: 1, padding: "12px 0", borderRadius: 12, fontSize: 14, fontWeight: 800,
                background: isSaving ? "#2d3748" : "linear-gradient(135deg, #38a169, #2f855a)",
                color: "white", border: "none", cursor: isSaving ? "default" : "pointer",
              }}>
                {isSaving ? "Saving..." : "Save Individual"}
              </button>
              <button onClick={() => setShowConfirmation(false)} style={{
                padding: "12px 20px", borderRadius: 12, fontSize: 13, fontWeight: 600,
                background: "transparent", color: "#718096",
                border: "1px solid rgba(99,179,237,0.08)", cursor: "pointer",
              }}>
                Edit More
              </button>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
      </div>

      {/* Input Bar */}
      <div style={{
        padding: "12px 20px 24px", background: "rgba(15,23,42,0.9)",
        borderTop: "1px solid rgba(99,179,237,0.08)",
        position: "sticky", bottom: 0,
      }}>
        {/* Data capture indicator */}
        {Object.keys(extractedData).filter(k => extractedData[k] && k !== "missingRequired" && k !== "nextQuestion").length > 0 && !showConfirmation && (
          <button onClick={() => setShowConfirmation(true)} style={{
            width: "100%", padding: "8px 12px", borderRadius: 10, marginBottom: 8,
            background: "rgba(79,209,197,0.06)", border: "1px solid rgba(79,209,197,0.12)",
            color: "#4fd1c5", fontSize: 11, fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}>
            <ChevronDown size={12} />
            {Object.keys(extractedData).filter(k => extractedData[k] && !["missingRequired", "nextQuestion"].includes(k)).length} fields captured — tap to review
          </button>
        )}

        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <button onClick={isListening ? stopListening : startListening} style={{
            width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: isListening ? "rgba(252,129,129,0.15)" : "rgba(99,179,237,0.08)",
            color: isListening ? "#fc8181" : "#63b3ed",
          }}>
            {isListening ? <MicOff size={20} /> : <Mic size={20} />}
          </button>

          <div style={{ flex: 1, position: "relative" }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={isListening ? "Listening..." : "Tell me about this individual..."}
              rows={1}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: 12, resize: "none",
                background: "rgba(15,23,42,0.7)", border: "1px solid rgba(99,179,237,0.08)",
                color: "#e2e8f0", fontSize: 14, outline: "none",
                maxHeight: 100, overflowY: "auto",
              }}
            />
          </div>

          <button onClick={handleSend} disabled={!input.trim() || isProcessing} style={{
            width: 44, height: 44, borderRadius: 12, border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: input.trim() && !isProcessing
              ? "linear-gradient(135deg, #3182ce, #2b6cb0)"
              : "rgba(99,179,237,0.08)",
            color: input.trim() && !isProcessing ? "white" : "#4a5568",
          }}>
            <Send size={18} />
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}